<script setup lang="ts">
import type { NavigationItem } from 'comark-content'
import type { ContentShas } from '#shared/types'
import { navigationPath, searchInstanceKeys } from '#shared/utils/content'
import { pullNavigationPath } from '#shared/utils/pull'

const colorMode = useColorMode()
const route = useRoute()
const { isAgentEnabled } = useNuxtAgent()
const isChatRoute = computed(() => route.path.startsWith('/dashboard/chat') || route.path.startsWith('/admin/analytics'))
const showAgent = computed(() => isAgentEnabled.value && !isChatRoute.value)

const { version: docsVersion } = useDocsVersion()
const { track } = useAnalytics()

const color = computed(() => colorMode.value === 'dark' ? '#020420' : 'white')

defineShortcuts({
  d: {
    handler: () => {
      colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'
    }
  }
})

watch(() => colorMode.preference, (newMode, oldMode) => {
  if (oldMode && newMode !== oldMode) {
    track('Color Mode Changed', { mode: newMode })
  }
})

const pull = usePullPreview()

const { data: navigation } = await useFetch<NavigationItem[]>(() =>
  pull.value ? pullNavigationPath(docsVersion.value, pull.value) : navigationPath(docsVersion.value), { key: 'navigation' })

const searchKeys = computed(() => searchInstanceKeys(docsVersion.value))
// Client-only: an SSR value gets baked into the page's ISR entry and would pin search to a stale commit.
const { data: searchShas } = useAsyncData(
  // A `computed()` key, not a getter: `experimental.extractAsyncDataHandlers` takes the first arrow
  // function as the handler, so a getter key is extracted instead (NUXT_E3008 in production).
  computed(() => `content-heads:${searchKeys.value.join(',')}`),
  () => $fetch<ContentShas>('/api/content/heads', {
    query: { keys: searchKeys.value.join(',') }
  }).catch((error) => {
    console.error('[search] could not resolve the content heads', error)
    return null
  }),
  { server: false, watch: [searchKeys] }
)

useHead({
  titleTemplate: title => title ? `${title} · Nuxt` : 'Nuxt: The Intuitive Web Framework',
  meta: [
    { key: 'theme-color', name: 'theme-color', content: color }
  ]
})

if (import.meta.server) {
  useHead({
    meta: [
      { name: 'viewport', content: 'width=device-width, initial-scale=1' }
    ],
    link: [
      { rel: 'icon', type: 'image/png', href: '/icon.png' }
    ],
    htmlAttrs: {
      lang: 'en'
    }
  })
  useSeoMeta({
    ogSiteName: 'Nuxt',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterSite: 'nuxt_js'
  })
  // Organization identity is provided via `schemaOrg.identity` in
  // nuxt.config.ts so the module can emit a single `#identity` node
  // (instead of a duplicated `#organization` graph entry). The WebSite
  // resolver inherits `url` from siteConfig but not `name`, so we wire
  // it up here against the same source of truth.
  useSchemaOrg([
    defineWebSite({
      name: useSiteConfig().name
    })
  ])
}

const navigationByVersion = computed(() => navigation.value ?? [])

provide('navigation', navigationByVersion)
provide('searchShas', searchShas)
</script>

<template>
  <UApp :tooltip="{ delayDuration: 300 }">
    <!-- <NuxtLoadingIndicator color="var(--ui-primary)  /> -->

    <div class="flex">
      <div class="flex-1 min-w-0">
        <NuxtLayout>
          <NuxtPage />
        </NuxtLayout>
      </div>

      <ClientOnly v-if="showAgent">
        <LazyAgentFloatingInput />
        <LazyAgentPanel />
      </ClientOnly>
    </div>

    <ClientOnly>
      <Search :navigation="navigationByVersion" />
    </ClientOnly>
  </UApp>
</template>

<style>
@media (min-width: 1024px) {
  .root {
    --ui-header-height: 112px;
  }
}
</style>
